// ─────────────────────────────────────────────────────────────────────────────
// App, the routing root.
//
// The split here is deliberate. Public pages (marketing, sign in, sign up,
// password reset) render directly, above the store scope and above every data
// context. Everything signed-in lives in AppShell.js, which is code-split and
// only fetched once a signed-in path is rendered.
//
// Before this split every public page mounted the entire provider tree, so the
// landing page opened authenticated connections it had no session for and
// logged CORS failures to every visitor, and the marketing bundle carried each
// workspace's configured store names. Both are gone: a visitor to the front
// page now downloads the marketing pages and nothing else.
// ─────────────────────────────────────────────────────────────────────────────
import './App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LandingPage from './pages/marketing/LandingPage';
import SignupPage from './pages/marketing/SignupPage';
import PricingPage from './pages/marketing/PricingPage';
import PrivacyTermsPage from './pages/marketing/PrivacyTermsPage';
import FaqPage from './pages/marketing/FaqPage';
import ContactSalesPage from './pages/marketing/ContactSalesPage';
import FeaturesPage from './pages/marketing/FeaturesPage';
import SolutionsPage from './pages/marketing/SolutionsPage';
import IntegrationsPage from './pages/marketing/IntegrationsPage';
import SecurityPage from './pages/marketing/SecurityPage';
import CustomersPage from './pages/marketing/CustomersPage';
import ChangelogPage from './pages/marketing/ChangelogPage';
import StatusPage from './pages/marketing/StatusPage';
import AboutPage from './pages/marketing/AboutPage';
import CareersPage from './pages/marketing/CareersPage';
import BlogPage from './pages/marketing/BlogPage';
import HowItWorksPage from './pages/marketing/HowItWorksPage';
import DpaPage from './pages/marketing/DpaPage';

const AppShell = lazy(() => import('./AppShell'));

// `/` is shared: the DEFAULT route for wavelengtherp.com is the public landing
// page, not the login screen. Signed-in users go straight to their dashboard.
function RootRoute() {
    const { user } = useAuth();
    return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

// Bare `/security` is ALWAYS the public marketing Security page, even signed in
// (clicking "Security" in the marketing header must never yank you into the
// app). Because it is declared here, above the `/*` shell, it wins outright.
// The in-app Security Center lives at /security/dashboard and its sibling views.
function App() {
    return (
        <BrowserRouter>
            <NotificationProvider>
            <AuthProvider>
                <Routes>
                    <Route path="/" element={<RootRoute />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    {/* Public marketing pages (must precede the app catch-all) */}
                    <Route path="/overview" element={<LandingPage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/solutions" element={<SolutionsPage />} />
                    <Route path="/integrations" element={<IntegrationsPage />} />
                    <Route path="/security" element={<SecurityPage />} />
                    <Route path="/how-it-works" element={<HowItWorksPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/careers" element={<CareersPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/changelog" element={<ChangelogPage />} />
                    <Route path="/status" element={<StatusPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/privacy" element={<PrivacyTermsPage />} />
                    <Route path="/terms" element={<PrivacyTermsPage />} />
                    <Route path="/dpa" element={<DpaPage />} />
                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/contact" element={<ContactSalesPage />} />
                    <Route path="/demo" element={<ContactSalesPage />} />
                    {/* Everything else is the signed-in application, code-split. */}
                    <Route
                        path="/*"
                        element={
                            <Suspense fallback={<div className="min-h-screen bg-[#F7F7F7]" />}>
                                <AppShell />
                            </Suspense>
                        }
                    />
                </Routes>
            </AuthProvider>
            </NotificationProvider>
        </BrowserRouter>
    );
}

export default App;
